import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import type { Waypoint } from "./utils/routeTotalDistance";
import { manualLeg } from "./utils/manualLeg";
import { routeTotalDistance } from "./utils/routeTotalDistance";
import { generateRouteGeoJSON } from "./utils/generateRouteGeoJSON";
import { generateId } from "./utils/idGenerator";
import { useVesselSettings } from "../vesselSettings/VesselSettingsContext";
/* eslint-disable react-refresh/only-export-components */

import {
  computeFairwayRoute,
  type RouteResult,
} from "../../utils/pathfinding/fairwayRouting";
import { useFairwayDataLoading } from "../../utils/pathfinding/useFairwayDataLoading";

const STORAGE_KEY = "routePlan";
const SAVED_SNAPSHOT_KEY = "routePlanSavedSnapshot";

export type RoutingMode = "off" | "auto" | "manual";

/** One point from a route loaded from API (full list before reducing to user waypoints). */
export interface LoadedRoutePoint {
  longitude: number;
  latitude: number;
  userpoint: boolean;
  fairway: boolean;
  tracked: boolean;
  isTerminal?: boolean;
}

interface RoutePlanState {
  waypoints: Waypoint[];
  /** Full list of points from last API load; null after clear or when not from load. */
  loadedRouteAllPoints: LoadedRoutePoint[] | null;
  routingMode: RoutingMode;
  totalDistance: number;
  routeName: string;
  savedRouteId: number | null;
  hasUserEdits: boolean;
  status: "idle" | "loading" | "error";
  error: string | null;
  focusedWaypointId: string | null;
  /** When false, waypoints must not be moved or removed (from API `editable: false`). */
  loadedRouteEditable: boolean;
}

type RoutePlanStoragePayload = Pick<
  RoutePlanState,
  | "waypoints"
  | "routingMode"
  | "totalDistance"
  | "routeName"
  | "savedRouteId"
  | "loadedRouteEditable"
  | "loadedRouteAllPoints"
>;

interface RoutePlanContextValue extends RoutePlanState {
  hasUnsavedChanges: boolean;
  markRouteSaved: (override?: Partial<RoutePlanSnapshot>) => void;
  setSavedRouteId: (routeId: number | null) => void;
  setFocusedWaypointId: (id: string | null) => void;
  addWaypoint: (
    lng: number,
    lat: number,
    name?: string,
    poiId?: string | null,
  ) => Promise<void>;
  insertWaypointBefore: (
    toWaypointId: string,
    lng: number,
    lat: number,
    name?: string,
    poiId?: string | null,
  ) => Promise<void>;
  removeWaypoint: (waypointId: string) => Promise<void>;
  relocateWaypoint: (
    waypointId: string,
    lngLat: [number, number],
  ) => Promise<void>;
  setRoutingMode: (mode: RoutingMode) => void;
  setRouteName: (name: string) => void;
  clearRoute: () => void;
  loadFromSmRoute: (route: {
    id?: number;
    name: string;
    length: number;
    editable?: boolean;
    waypoints: Array<{
      fairway: boolean;
      latitude: number;
      longitude: number;
      userpoint?: boolean;
      tracked?: boolean;
    }>;
  }) => Promise<void>;
  setLoadedRouteEditable: (editable: boolean) => void;
  routeGeoJSON: ReturnType<typeof generateRouteGeoJSON>;
}

const RoutePlanContext = createContext<RoutePlanContextValue | null>(null);

const initialState: RoutePlanState = {
  waypoints: [],
  loadedRouteAllPoints: null,
  routingMode: "off",
  totalDistance: 0,
  routeName: "",
  savedRouteId: null,
  hasUserEdits: false,
  status: "idle",
  error: null,
  focusedWaypointId: null,
  loadedRouteEditable: true,
};

const noop = () => {};
const asyncNoop = async () => {};

const defaultRoutePlanContextValue: RoutePlanContextValue = {
  ...initialState,
  hasUnsavedChanges: false,
  markRouteSaved: noop,
  setSavedRouteId: noop,
  addWaypoint: asyncNoop,
  insertWaypointBefore: asyncNoop,
  removeWaypoint: asyncNoop,
  relocateWaypoint: asyncNoop,
  setRoutingMode: noop,
  setRouteName: noop,
  setFocusedWaypointId: noop,
  clearRoute: noop,
  loadFromSmRoute: asyncNoop as RoutePlanContextValue["loadFromSmRoute"],
  setLoadedRouteEditable: noop,
  routeGeoJSON: generateRouteGeoJSON([]),
};

function inferRoutingModeFromWaypoints(waypoints: Waypoint[]): RoutingMode {
  if (waypoints.length === 0) return "off";
  const modes = new Set(waypoints.map((wp) => wp.routingMode));
  if (modes.size === 1 && modes.has("auto")) return "auto";
  return "manual";
}

function haversineDistanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function lineDistanceMeters(coords: number[][]): number {
  let distance = 0;
  for (let idx = 1; idx < coords.length; idx += 1) {
    const [lng1, lat1] = coords[idx - 1] ?? [];
    const [lng2, lat2] = coords[idx] ?? [];
    if (
      typeof lng1 !== "number" ||
      typeof lat1 !== "number" ||
      typeof lng2 !== "number" ||
      typeof lat2 !== "number"
    ) {
      continue;
    }
    distance += haversineDistanceMeters(lat1, lng1, lat2, lng2);
  }
  return distance;
}

function loadRoutePlanFromStorage(): Partial<RoutePlanState> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    // Validate and return only the fields we want to restore
    const coerceNumberOrNull = (raw: unknown): number | null => {
      // Number(null) === 0; treat missing / non-positive values as unsaved.
      if (raw == null || raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    };
    return {
      waypoints: Array.isArray(parsed.waypoints)
        ? parsed.waypoints.map((wp: Waypoint) => ({
            ...wp,
            userpoint: wp.userpoint !== false,
            tracked: wp.tracked === true,
          }))
        : [],
      routingMode:
        parsed.routingMode === "off" ||
        parsed.routingMode === "auto" ||
        parsed.routingMode === "manual"
          ? parsed.routingMode
          : "off",
      totalDistance:
        typeof parsed.totalDistance === "number" ? parsed.totalDistance : 0,
      routeName: typeof parsed.routeName === "string" ? parsed.routeName : "",
      savedRouteId: coerceNumberOrNull(parsed.savedRouteId),
      loadedRouteEditable:
        typeof parsed.loadedRouteEditable === "boolean"
          ? parsed.loadedRouteEditable
          : true,
      loadedRouteAllPoints: Array.isArray(parsed.loadedRouteAllPoints)
        ? parsed.loadedRouteAllPoints
            .map((p: Partial<LoadedRoutePoint>) => ({
              longitude: Number(p.longitude),
              latitude: Number(p.latitude),
              userpoint: p.userpoint !== false,
              fairway: p.fairway === true,
              tracked: p.tracked === true,
              isTerminal: p.isTerminal === true,
            }))
            .filter(
              (p: LoadedRoutePoint) =>
                Number.isFinite(p.longitude) && Number.isFinite(p.latitude),
            )
        : null,
    };
  } catch {
    return {};
  }
}

type RoutePlanSnapshot = Pick<
  RoutePlanState,
  "waypoints" | "routingMode" | "totalDistance" | "routeName"
>;

function buildRoutePlanSnapshot(snapshot: RoutePlanSnapshot): string {
  return JSON.stringify(snapshot);
}

function loadSavedSnapshotFromStorage(): string | null {
  try {
    return localStorage.getItem(SAVED_SNAPSHOT_KEY);
  } catch {
    return null;
  }
}

function saveSavedSnapshotToStorage(snapshot: string): void {
  try {
    localStorage.setItem(SAVED_SNAPSHOT_KEY, snapshot);
  } catch {
    // Ignore storage failures
  }
}

function saveRoutePlanToStorage(payload: RoutePlanStoragePayload): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures
  }
}

export function RoutePlanProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { vesselDraft, vesselHeight } = useVesselSettings();
  const fairwayDataLoading = useFairwayDataLoading();

  // Initialize state from localStorage
  const [state, setState] = useState<RoutePlanState>(() => {
    const loaded = loadRoutePlanFromStorage();
    return {
      ...initialState,
      ...loaded,
    };
  });
  const [savedSnapshot, setSavedSnapshot] = useState<string>(
    () =>
      loadSavedSnapshotFromStorage() ??
      buildRoutePlanSnapshot({
        waypoints: [],
        routingMode: "off",
        totalDistance: 0,
        routeName: "",
      }),
  );

  // Ref to track latest state for use in callbacks
  const stateRef = useRef<RoutePlanState>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Reset auto routing mode if data is not loaded
  useEffect(() => {
    if (state.routingMode === "auto" && !fairwayDataLoading.isLoaded) {
      // If data isn't loaded, switch to manual mode if there are waypoints, otherwise off
      const newMode = state.waypoints.length > 0 ? "manual" : "off";
      setState((prev) => ({ ...prev, routingMode: newMode }));
    }
  }, [fairwayDataLoading.isLoaded, state.routingMode, state.waypoints.length]);

  const routePlanStorage = useMemo<RoutePlanStoragePayload>(
    () => ({
      waypoints: state.waypoints,
      routingMode: state.routingMode,
      totalDistance: state.totalDistance,
      routeName: state.routeName,
      savedRouteId: state.savedRouteId,
      loadedRouteEditable: state.loadedRouteEditable,
      loadedRouteAllPoints: state.loadedRouteAllPoints,
    }),
    [
      state.waypoints,
      state.routingMode,
      state.totalDistance,
      state.routeName,
      state.savedRouteId,
      state.loadedRouteEditable,
      state.loadedRouteAllPoints,
    ],
  );

  // Save to localStorage whenever persistent fields change
  useEffect(() => {
    saveRoutePlanToStorage(routePlanStorage);
  }, [routePlanStorage]);

  const currentSnapshot = useMemo(
    () =>
      buildRoutePlanSnapshot({
        waypoints: state.waypoints,
        routingMode: state.routingMode,
        totalDistance: state.totalDistance,
        routeName: state.routeName,
      }),
    [state.waypoints, state.routingMode, state.totalDistance, state.routeName],
  );
  const hasUnsavedChanges = currentSnapshot !== savedSnapshot;

  const markRouteSaved = useCallback(
    (override?: Partial<RoutePlanSnapshot>) => {
      const base: RoutePlanSnapshot = {
        waypoints: stateRef.current.waypoints,
        routingMode: stateRef.current.routingMode,
        totalDistance: stateRef.current.totalDistance,
        routeName: stateRef.current.routeName,
      };
      const snapshot = buildRoutePlanSnapshot({ ...base, ...(override ?? {}) });
      setSavedSnapshot(snapshot);
      saveSavedSnapshotToStorage(snapshot);
    },
    [],
  );

  const setSavedRouteId = useCallback((routeId: number | null) => {
    setState((prev) => ({ ...prev, savedRouteId: routeId }));
  }, []);

  const setLoadedRouteEditable = useCallback((editable: boolean) => {
    setState((prev) => ({ ...prev, loadedRouteEditable: editable }));
  }, []);

  const computeLeg = useCallback(
    async ({
      fromWaypointId,
      fromLngLat,
      toWaypointId,
      toLngLat,
      routingMode,
    }: {
      fromWaypointId: string;
      fromLngLat: [number, number];
      toWaypointId: string;
      toLngLat: [number, number];
      routingMode: Waypoint["routingMode"];
    }): Promise<{ passage: Waypoint["passage"]; distance: number }> => {
      const [fromLng, fromLat] = fromLngLat;
      const [toLng, toLat] = toLngLat;

      if (routingMode === "auto") {
        try {
          const route: RouteResult = await computeFairwayRoute(
            [fromLng, fromLat],
            [toLng, toLat],
            vesselDraft,
            vesselHeight,
          );

          return {
            passage: [
              {
                type: "Feature",
                properties: {
                  distance: route.distanceMeters,
                  routingMode: "auto",
                  fromWaypointId,
                  toWaypointId,
                },
                geometry: {
                  type: "LineString",
                  coordinates: route.line.geometry.coordinates,
                },
              },
            ],
            distance: route.distanceMeters,
          };
        } catch {
          // Fallback to manual if auto fails (consistent with relocate/remove behavior)
        }
      }

      const result = manualLeg({
        sourceLat: fromLat,
        sourceLng: fromLng,
        destinationLat: toLat,
        destinationLng: toLng,
      });

      return {
        passage: result.geoJson.map((f) => ({
          ...f,
          properties: {
            ...f.properties,
            routingMode: "manual",
            fromWaypointId,
            toWaypointId,
          },
        })),
        distance: result.distance,
      };
    },
    [vesselDraft, vesselHeight],
  );

  const addWaypoint = useCallback(
    async (lng: number, lat: number, name?: string, poiId?: string | null) => {
      if (state.routingMode === "off") return;

      setState((prev) => ({ ...prev, status: "loading", error: null }));

      try {
        let passage: Waypoint["passage"] = [];
        let distance = 0;

        // If there's a previous waypoint, calculate the leg
        if (state.waypoints.length > 0) {
          const prevWaypoint = state.waypoints[state.waypoints.length - 1];
          const newWaypointId = generateId();

          // Compute leg from previous waypoint to the new waypoint id (need id for tagging)
          const legRoutingMode: Waypoint["routingMode"] =
            state.routingMode === "auto" ? "auto" : "manual";
          if (legRoutingMode === "auto") {
            // In addWaypoint, keep previous behavior: if auto routing fails, surface error (no silent fallback)
            try {
              const route: RouteResult = await computeFairwayRoute(
                prevWaypoint.lngLat,
                [lng, lat],
                vesselDraft,
                vesselHeight,
              );
              passage = [
                {
                  type: "Feature",
                  properties: {
                    distance: route.distanceMeters,
                    routingMode: "auto",
                    fromWaypointId: prevWaypoint.id,
                    toWaypointId: newWaypointId,
                  },
                  geometry: {
                    type: "LineString",
                    coordinates: route.line.geometry.coordinates,
                  },
                },
              ];
              distance = route.distanceMeters;
            } catch {
              setState((prev) => ({
                ...prev,
                status: "error",
                error: t("routePlan.routingAutoFailed"),
              }));
              return;
            }
          } else {
            const result = manualLeg({
              sourceLat: prevWaypoint.lngLat[1],
              sourceLng: prevWaypoint.lngLat[0],
              destinationLat: lat,
              destinationLng: lng,
            });
            passage = result.geoJson.map((f) => ({
              ...f,
              properties: {
                ...f.properties,
                routingMode: "manual",
                fromWaypointId: prevWaypoint.id,
                toWaypointId: newWaypointId,
              },
            }));
            distance = result.distance;
          }

          const waypoint: Waypoint = {
            id: newWaypointId,
            name: name || "",
            lngLat: [lng, lat],
            poiId: poiId || null,
            userpoint: true,
            tracked: false,
            routingMode: legRoutingMode,
            distance,
            passage,
          };

          const newWaypoints = [...state.waypoints, waypoint];
          const totalDistance = routeTotalDistance(newWaypoints);

          setState((prev) => ({
            ...prev,
            waypoints: newWaypoints,
            totalDistance,
            hasUserEdits: true,
            loadedRouteAllPoints: null,
            status: "idle",
            error: null,
          }));
          return;
        }

        const waypoint: Waypoint = {
          id: generateId(),
          name: name || "",
          lngLat: [lng, lat],
          poiId: poiId || null,
          userpoint: true,
          tracked: false,
          routingMode: state.routingMode === "auto" ? "auto" : "manual",
          distance,
          passage,
        };

        const newWaypoints = [...state.waypoints, waypoint];
        const totalDistance = routeTotalDistance(newWaypoints);

        setState((prev) => ({
          ...prev,
          waypoints: newWaypoints,
          totalDistance,
          hasUserEdits: true,
          loadedRouteAllPoints: null,
          status: "idle",
          error: null,
        }));
      } catch {
        setState((prev) => ({
          ...prev,
          status: "error",
          error: t("routePlan.addWaypointFailed"),
        }));
      }
    },
    [state.waypoints, state.routingMode, t, vesselDraft, vesselHeight],
  );

  const insertWaypointBefore = useCallback(
    async (
      toWaypointId: string,
      lng: number,
      lat: number,
      name?: string,
      poiId?: string | null,
    ) => {
      if (!stateRef.current.loadedRouteEditable) {
        setState((prev) => ({ ...prev, status: "idle" }));
        return;
      }

      setState((prev) => ({ ...prev, status: "loading", error: null }));

      try {
        const currentState = stateRef.current;
        const currentWaypoints = [...currentState.waypoints];

        const toIdx = currentWaypoints.findIndex(
          (wp) => wp.id === toWaypointId,
        );
        const fromIdx = toIdx - 1;
        if (toIdx < 0 || fromIdx < 0) {
          setState((prev) => ({ ...prev, status: "idle" }));
          return;
        }

        const fromWp = currentWaypoints[fromIdx];
        const toWp = currentWaypoints[toIdx];

        // Use the leg's mode (the "to waypoint" mode) for the inserted waypoint as well.
        const newWaypointId = generateId();
        const legMode: Waypoint["routingMode"] = toWp.routingMode;

        const newWp: Waypoint = {
          id: newWaypointId,
          name: name || "",
          lngLat: [lng, lat],
          poiId: poiId || null,
          userpoint: true,
          tracked: false,
          routingMode: legMode,
          distance: 0,
          passage: [],
        };

        // Recompute: fromWp -> newWp
        const leg1 = await computeLeg({
          fromWaypointId: fromWp.id,
          fromLngLat: fromWp.lngLat,
          toWaypointId: newWp.id,
          toLngLat: newWp.lngLat,
          routingMode: legMode,
        });
        newWp.passage = leg1.passage;
        newWp.distance = leg1.distance;

        // Recompute: newWp -> toWp (updates the toWp's incoming leg)
        const leg2 = await computeLeg({
          fromWaypointId: newWp.id,
          fromLngLat: newWp.lngLat,
          toWaypointId: toWp.id,
          toLngLat: toWp.lngLat,
          routingMode: toWp.routingMode,
        });
        toWp.passage = leg2.passage;
        toWp.distance = leg2.distance;

        const newWaypoints = [
          ...currentWaypoints.slice(0, toIdx),
          newWp,
          toWp,
          ...currentWaypoints.slice(toIdx + 1),
        ];

        const totalDistance = routeTotalDistance(newWaypoints);
        setState((prev) => ({
          ...prev,
          waypoints: newWaypoints,
          totalDistance,
          hasUserEdits: true,
          loadedRouteAllPoints: null,
          status: "idle",
          error: null,
        }));
      } catch {
        setState((prev) => ({
          ...prev,
          status: "error",
          error: t("routePlan.insertWaypointFailed"),
        }));
      }
    },
    [computeLeg, t],
  );

  const removeWaypoint = useCallback(
    async (waypointId: string) => {
      if (!stateRef.current.loadedRouteEditable) {
        setState((prev) => ({ ...prev, status: "idle" }));
        return;
      }

      setState((prev) => ({ ...prev, status: "loading", error: null }));

      try {
        // Get current state from ref
        const currentState = stateRef.current;
        const currentWaypoints = [...currentState.waypoints];

        const wpIndex = currentWaypoints.findIndex(
          (wp) => wp.id === waypointId,
        );
        if (wpIndex < 0) {
          setState((prev) => ({ ...prev, status: "idle" }));
          return;
        }

        const target = currentWaypoints[wpIndex];
        if (target?.tracked) {
          setState((prev) => ({ ...prev, status: "idle" }));
          return;
        }

        // Remove the waypoint
        const newWaypoints = currentWaypoints.filter(
          (wp) => wp.id !== waypointId,
        );

        // Handle different removal scenarios
        if (wpIndex === 0) {
          // Removing the first waypoint: clear passage and distance of the new first waypoint
          if (newWaypoints.length > 0) {
            newWaypoints[0].passage = [];
            newWaypoints[0].distance = 0;
          }
        } else if (wpIndex > 0 && wpIndex < currentWaypoints.length - 1) {
          // Removing a middle waypoint: recalculate the leg between previous and next
          const prevWp = newWaypoints[wpIndex - 1];
          const nextWp = newWaypoints[wpIndex]; // This is now at the index where the removed one was
          const leg = await computeLeg({
            fromWaypointId: prevWp.id,
            fromLngLat: prevWp.lngLat,
            toWaypointId: nextWp.id,
            toLngLat: nextWp.lngLat,
            routingMode: nextWp.routingMode,
          });

          nextWp.passage = leg.passage;
          nextWp.distance = leg.distance;
        }
        // If removing the last waypoint, no recalculation needed (the previous waypoint's passage stays as is)

        const totalDistance = routeTotalDistance(newWaypoints);

        setState((prev) => ({
          ...prev,
          waypoints: newWaypoints,
          totalDistance,
          hasUserEdits: true,
          loadedRouteAllPoints: null,
          status: "idle",
          error: null,
        }));
      } catch {
        setState((prev) => ({
          ...prev,
          status: "error",
          error: t("routePlan.removeWaypointFailed"),
        }));
      }
    },
    [computeLeg, t],
  );

  const relocateWaypoint = useCallback(
    async (waypointId: string, lngLat: [number, number]) => {
      if (!stateRef.current.loadedRouteEditable) {
        setState((prev) => ({ ...prev, status: "idle" }));
        return;
      }

      setState((prev) => ({ ...prev, status: "loading", error: null }));

      try {
        // Use latest state snapshot to avoid setState-as-getter patterns.
        const currentWaypoints: Waypoint[] = [...stateRef.current.waypoints];

        const wpIndex = currentWaypoints.findIndex(
          (wp) => wp.id === waypointId,
        );
        if (wpIndex < 0) {
          setState((prev) => ({ ...prev, status: "idle" }));
          return;
        }

        const original = currentWaypoints[wpIndex];
        if (original?.tracked) {
          setState((prev) => ({ ...prev, status: "idle" }));
          return;
        }

        const newWaypoints = [...currentWaypoints];
        const prevWp = wpIndex > 0 ? newWaypoints[wpIndex - 1] : null;
        const nextWp =
          wpIndex < newWaypoints.length - 1 ? newWaypoints[wpIndex + 1] : null;
        const [lng, lat] = lngLat;

        // Always replace the moved waypoint object so downstream memoization sees a change.
        const movedWp: Waypoint = {
          ...newWaypoints[wpIndex],
          lngLat,
          ...(wpIndex === 0 ? { passage: [], distance: 0 } : {}),
        };

        // Recalculate leg *to* moved waypoint (stored on moved waypoint)
        if (prevWp) {
          const leg = await computeLeg({
            fromWaypointId: prevWp.id,
            fromLngLat: prevWp.lngLat,
            toWaypointId: movedWp.id,
            toLngLat: [lng, lat],
            routingMode: movedWp.routingMode,
          });
          movedWp.passage = leg.passage;
          movedWp.distance = leg.distance;
        }

        // Recalculate leg *to* next waypoint (stored on next waypoint)
        if (nextWp) {
          const leg = await computeLeg({
            fromWaypointId: movedWp.id,
            fromLngLat: [lng, lat],
            toWaypointId: nextWp.id,
            toLngLat: nextWp.lngLat,
            // The leg leading to next waypoint is defined by next waypoint's routing mode.
            routingMode: nextWp.routingMode,
          });
          newWaypoints[wpIndex + 1] = {
            ...nextWp,
            passage: leg.passage,
            distance: leg.distance,
          };
        }

        newWaypoints[wpIndex] = movedWp;

        const totalDistance = routeTotalDistance(newWaypoints);

        setState((prev) => ({
          ...prev,
          waypoints: newWaypoints,
          totalDistance,
          hasUserEdits: true,
          loadedRouteAllPoints: null,
          status: "idle",
          error: null,
        }));
      } catch {
        setState((prev) => ({
          ...prev,
          status: "error",
          error: t("routePlan.relocateWaypointFailed"),
        }));
      }
    },
    [computeLeg, t],
  );

  const setRoutingMode = useCallback(
    (mode: RoutingMode) => {
      // Prevent enabling auto routing if data is not loaded
      if (mode === "auto" && !fairwayDataLoading.isLoaded) {
        return;
      }
      setState((prev) => ({
        ...prev,
        routingMode: mode,
        hasUserEdits: true,
      }));
    },
    [fairwayDataLoading.isLoaded],
  );

  const setRouteName = useCallback((name: string) => {
    setState((prev) => ({
      ...prev,
      routeName: name,
      hasUserEdits: true,
    }));
  }, []);

  const setFocusedWaypointId = useCallback((id: string | null) => {
    setState((prev) => ({ ...prev, focusedWaypointId: id }));
  }, []);

  const clearRoute = useCallback(() => {
    setState((prev) => ({
      ...initialState,
      routingMode: prev.routingMode,
    }));
    // Also clear from localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage failures
    }
  }, []);

  const loadFromSmRoute = useCallback(
    async (route: {
      id?: number | null;
      name: string;
      length: number;
      editable?: boolean;
      waypoints: Array<{
        order_id?: number;
        fairway: boolean;
        latitude: number;
        longitude: number;
        userpoint?: boolean;
        tracked?: boolean;
      }>;
    }) => {
      const loadedRouteEditable = route.editable !== false;
      const isDev = import.meta.env.DEV;
      const createCoordKey = (longitude: number, latitude: number) =>
        `${longitude.toFixed(6)},${latitude.toFixed(6)}`;
      const collectDuplicateCoordKeys = (
        points: Array<{ longitude: number; latitude: number }>,
      ) => {
        const counts = new Map<string, number>();
        for (const point of points) {
          if (
            typeof point.longitude !== "number" ||
            typeof point.latitude !== "number"
          ) {
            continue;
          }
          const key = createCoordKey(point.longitude, point.latitude);
          counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        return Array.from(counts.entries())
          .filter(([, count]) => count > 1)
          .map(([key, count]) => ({ key, count }));
      };

      if (isDev) {
        console.groupCollapsed(
          `[RoutePlanDebug] loadFromSmRoute routeId=${String(route.id ?? "null")} name="${route.name}"`,
        );
        console.log("[RoutePlanDebug] raw route payload:", route);
      }
      if (isDev && Array.isArray(route.waypoints)) {
        console.table(
          route.waypoints.map((wp, index) => ({
            index,
            order_id:
              typeof wp.order_id === "number" ? wp.order_id : "(missing)",
            longitude: wp.longitude,
            latitude: wp.latitude,
            userpoint: wp.userpoint,
            fairway: wp.fairway,
          })),
        );
        const rawDuplicateCoords = collectDuplicateCoordKeys(route.waypoints);
        if (rawDuplicateCoords.length > 0) {
          console.warn(
            "[RoutePlanDebug] duplicate coordinates in raw payload:",
            rawDuplicateCoords,
          );
        }
      }
      setState((prev) => ({ ...prev, status: "loading", error: null }));

      try {
        const incoming = (Array.isArray(route.waypoints) ? route.waypoints : [])
          .map((wp, index) => ({ wp, index }))
          .sort((a, b) => {
            const aHasOrder = typeof a.wp.order_id === "number";
            const bHasOrder = typeof b.wp.order_id === "number";

            if (aHasOrder && bHasOrder) {
              const diff =
                (a.wp.order_id as number) - (b.wp.order_id as number);
              return diff !== 0 ? diff : a.index - b.index;
            }

            return a.index - b.index;
          })
          .map(({ wp }) => wp);
        if (isDev) {
          console.table(
            incoming.map((wp, index) => ({
              index,
              order_id:
                typeof wp.order_id === "number" ? wp.order_id : "(missing)",
              longitude: wp.longitude,
              latitude: wp.latitude,
              userpoint: wp.userpoint,
              fairway: wp.fairway,
            })),
          );
        }
        if (incoming.length === 0) {
          const nextRouteName = route.name ?? stateRef.current.routeName ?? "";
          const savedRouteId = route.id == null ? null : Number(route.id);
          const safeSavedRouteId =
            Number.isFinite(savedRouteId) ? savedRouteId : null;
          const snapshot = buildRoutePlanSnapshot({
            waypoints: [],
            routingMode: "off",
            totalDistance: 0,
            routeName: nextRouteName,
          });
          setSavedSnapshot(snapshot);
          saveSavedSnapshotToStorage(snapshot);
          setState((prev) => ({
            ...prev,
            waypoints: [],
            loadedRouteAllPoints: null,
            totalDistance: 0,
            routeName: nextRouteName,
            savedRouteId: safeSavedRouteId,
            routingMode: "off",
            hasUserEdits: false,
            status: "idle",
            error: null,
            loadedRouteEditable,
          }));
          return;
        }

        const normalized: LoadedRoutePoint[] = incoming
          .map((wp, index) => ({
            longitude: wp.longitude,
            latitude: wp.latitude,
            userpoint: wp.userpoint !== false,
            fairway: wp.fairway === true,
            tracked: wp.tracked === true,
            isTerminal: index === 0 || index === incoming.length - 1,
          }))
          .filter(
            (wp) =>
              typeof wp.longitude === "number" &&
              typeof wp.latitude === "number",
          );
        if (isDev) {
          console.table(
            normalized.map((wp, index) => ({
              index,
              longitude: wp.longitude,
              latitude: wp.latitude,
              userpoint: wp.userpoint,
              fairway: wp.fairway,
              isTerminal: wp.isTerminal,
            })),
          );
          const normalizedDuplicateCoords = collectDuplicateCoordKeys(normalized);
          if (normalizedDuplicateCoords.length > 0) {
            console.warn(
              "[RoutePlanDebug] duplicate coordinates after normalization:",
              normalizedDuplicateCoords,
            );
          }
        }

        if (normalized.length === 0) {
          const nextRouteName = route.name ?? stateRef.current.routeName ?? "";
          const savedRouteId = route.id == null ? null : Number(route.id);
          const safeSavedRouteId =
            Number.isFinite(savedRouteId) ? savedRouteId : null;
          const snapshot = buildRoutePlanSnapshot({
            waypoints: [],
            routingMode: "off",
            totalDistance: 0,
            routeName: nextRouteName,
          });
          setSavedSnapshot(snapshot);
          saveSavedSnapshotToStorage(snapshot);
          setState((prev) => ({
            ...prev,
            waypoints: [],
            loadedRouteAllPoints: null,
            totalDistance: 0,
            routeName: nextRouteName,
            savedRouteId: safeSavedRouteId,
            routingMode: "off",
            hasUserEdits: false,
            status: "idle",
            error: null,
            loadedRouteEditable,
          }));
          return;
        }

        const lastIncomingIdx = normalized.length - 1;
        const userpointIndices: number[] = [];
        for (let idx = 0; idx < normalized.length; idx += 1) {
          const entry = normalized[idx];
          if (idx === 0 || idx === lastIncomingIdx || entry.userpoint) {
            userpointIndices.push(idx);
          }
        }
        if (isDev) {
          console.log("[RoutePlanDebug] userpointIndices:", userpointIndices);
        }

        const waypoints: Waypoint[] = userpointIndices.map((idx) => {
          const entry = normalized[idx];
          return {
            id: generateId(),
            name: "",
            lngLat: [entry.longitude, entry.latitude],
            poiId: null,
            userpoint: !!(entry.userpoint || entry.isTerminal),
            tracked: entry.tracked,
            routingMode: entry.fairway ? "auto" : "manual",
            distance: 0,
            passage: [],
          };
        });
        if (isDev) {
          const finalDuplicateCoords = collectDuplicateCoordKeys(
            waypoints.map((wp) => ({
              longitude: wp.lngLat[0],
              latitude: wp.lngLat[1],
            })),
          );
          if (finalDuplicateCoords.length > 0) {
            console.warn(
              "[RoutePlanDebug] duplicate coordinates in final waypoints:",
              finalDuplicateCoords,
            );
          }
        }

        for (let idx = 1; idx < userpointIndices.length; idx += 1) {
          const startIdx = userpointIndices[idx - 1];
          const endIdx = userpointIndices[idx];
          const coords = normalized
            .slice(startIdx, endIdx + 1)
            .map((entry) => [entry.longitude, entry.latitude]);
          const fromWp = waypoints[idx - 1];
          const toWp = waypoints[idx];
          const distance =
            coords.length >= 2
              ? lineDistanceMeters(coords)
              : manualLeg({
                  sourceLat: fromWp.lngLat[1],
                  sourceLng: fromWp.lngLat[0],
                  destinationLat: toWp.lngLat[1],
                  destinationLng: toWp.lngLat[0],
                }).distance;

          toWp.passage = [
            {
              type: "Feature",
              properties: {
                distance,
                routingMode: toWp.routingMode,
                fromWaypointId: fromWp.id,
                toWaypointId: toWp.id,
              },
              geometry: {
                type: "LineString",
                coordinates:
                  coords.length >= 2
                    ? coords
                    : [
                        [fromWp.lngLat[0], fromWp.lngLat[1]],
                        [toWp.lngLat[0], toWp.lngLat[1]],
                      ],
              },
            },
          ];
          toWp.distance = distance;
        }

        const totalDistance = routeTotalDistance(waypoints);
        const routingMode = inferRoutingModeFromWaypoints(waypoints);
        const nextRouteName = route.name ?? stateRef.current.routeName ?? "";
        const savedRouteId = route.id == null ? null : Number(route.id);
        const safeSavedRouteId =
          Number.isFinite(savedRouteId) ? savedRouteId : null;
        const snapshot = buildRoutePlanSnapshot({
          waypoints,
          totalDistance,
          routingMode,
          routeName: nextRouteName,
        });
        setSavedSnapshot(snapshot);
        saveSavedSnapshotToStorage(snapshot);

        setState((prev) => ({
          ...prev,
          waypoints,
          loadedRouteAllPoints: normalized,
          totalDistance,
          routeName: nextRouteName,
          savedRouteId: safeSavedRouteId,
          routingMode,
          hasUserEdits: false,
          status: "idle",
          error: null,
          loadedRouteEditable,
        }));
      } catch {
        setState((prev) => ({
          ...prev,
          status: "error",
          error: t("routePlan.loadRouteFailed"),
        }));
      } finally {
        if (isDev) {
          console.groupEnd();
        }
      }
    },
    [t],
  );

  const routeGeoJSON = useMemo(
    () => generateRouteGeoJSON(state.waypoints),
    [state.waypoints],
  );

  // Memoize context value with individual state fields as dependencies
  // This prevents unnecessary re-renders when only transient fields (status, error) change
  // Components that only need persistent fields won't re-render on status/error updates
  const value = useMemo<RoutePlanContextValue>(
    () => ({
      waypoints: state.waypoints,
      loadedRouteAllPoints: state.loadedRouteAllPoints,
      routingMode: state.routingMode,
      totalDistance: state.totalDistance,
      routeName: state.routeName,
      savedRouteId: state.savedRouteId,
      hasUserEdits: state.hasUserEdits,
      status: state.status,
      error: state.error,
      focusedWaypointId: state.focusedWaypointId,
      loadedRouteEditable: state.loadedRouteEditable,
      addWaypoint,
      insertWaypointBefore,
      removeWaypoint,
      relocateWaypoint,
      setRoutingMode,
      setRouteName,
      setSavedRouteId,
      setFocusedWaypointId,
      clearRoute,
      loadFromSmRoute,
      setLoadedRouteEditable,
      hasUnsavedChanges,
      markRouteSaved,
      routeGeoJSON,
    }),
    [
      // Individual state fields instead of entire state object
      state.waypoints,
      state.loadedRouteAllPoints,
      state.routingMode,
      state.totalDistance,
      state.routeName,
      state.savedRouteId,
      state.hasUserEdits,
      state.status,
      state.error,
      state.focusedWaypointId,
      state.loadedRouteEditable,
      // Action callbacks (already memoized with useCallback)
      addWaypoint,
      insertWaypointBefore,
      removeWaypoint,
      relocateWaypoint,
      setRoutingMode,
      setRouteName,
      setSavedRouteId,
      setFocusedWaypointId,
      clearRoute,
      loadFromSmRoute,
      setLoadedRouteEditable,
      // Computed values
      hasUnsavedChanges,
      markRouteSaved,
      routeGeoJSON,
    ],
  );

  return (
    <RoutePlanContext.Provider value={value}>
      {children}
    </RoutePlanContext.Provider>
  );
}

export function useRoutePlan() {
  const ctx = useContext(RoutePlanContext);
  if (!ctx) {
    // Fallback for environments where the provider is not mounted
    // (e.g. Storybook stories, isolated tests). Returns a safe, no-op
    // implementation so components can render without crashing.
    return defaultRoutePlanContextValue;
  }
  return ctx;
}
